import contract from '../../../catalogs/janus/row-contract.json';
import { fromBaseRecord, fromBaseUuid } from '../compile/from-base-context';
import { readItemField } from '../compile/item-field-reader';
import { readInventoryField } from '../compile/inventory-raw-readers';
import type { Row, SpawnWrite } from '../spawn/row-plan';

type Kind = 'inventory' | 'status' | 'template' | 'member';
interface GeneratedCommand {
    schema: string; operation_id: string; destination_id?: string; caster_id?: string; summary: string;
    rows?: { kind: Kind; row: Row }[]; inserts?: { kind: Kind; row: Row }[]; updates?: SpawnWrite[];
}
const kinds = {
    inventory: { ...contract.inventory, columns: contract.inventory.columns.filter(key => !['inventory_private', 'inventory_deleted'].includes(key)) },
    status: { ...contract.statuses, columns: contract.statuses.columns.filter(key => !['status_private', 'status_campaign_id', 'status_deleted'].includes(key)) },
    template: { key: 'enchantment_template_id', columns: ['enchantment_template_id', 'enchantment_template_name'], numbers: [] },
    member: { key: 'enchantment_id', columns: ['enchantment_id', 'enchantment_template_id', 'enchantment_type', 'enchantment_item_id', 'enchantment_name'], numbers: [] },
} satisfies Record<Kind, { key: string; columns: string[]; numbers: string[] }>;

/** Run once on the completed native plan, including every composed template and status. */
export function assertGeneratorValueContract(command: GeneratedCommand): void {
    const generator = command.schema === 'random-generator.v1';
    if ((!generator && command.schema !== 'add-caster-inventory.v1') || !fromBaseUuid(command.operation_id)
        || !fromBaseUuid(generator ? command.destination_id : command.caster_id) || typeof command.summary !== 'string')
        throw new Error('generator:invalid-command');
    const rows = generator ? command.rows : command.inserts;
    if (!Array.isArray(rows)) throw new Error('generator:invalid-command');
    const seen = new Set<string>();
    const values = (kind: Kind, row: Row, allowed: readonly string[]): void => {
        const spec: { numbers: readonly string[]; decimals?: readonly string[] } = kinds[kind];
        for (const [key, leaf] of Object.entries(row)) {
            if (!allowed.includes(key)) throw new Error('generator:invalid-columns');
            const value = kind === 'inventory' ? readInventoryField({ inventory: row }, key) : readItemField(row, key);
            // Wire rows have scalar leaves; held-state readers may unwrap legacy arrays.
            if (leaf !== null && typeof leaf === 'object') throw new Error(`generator:invalid-storage-type:${kind}:${key}`);
            if (value == null) continue;
            if (spec.numbers.includes(key) ? typeof value !== 'number' || !Number.isFinite(value)
                || !(spec.decimals ?? []).includes(key) && !Number.isInteger(value) : typeof value !== 'string')
                throw new Error(`generator:invalid-storage-type:${kind}:${key}`);
        }
    };
    for (const operation of rows) {
        if (!fromBaseRecord(operation) || !Object.prototype.hasOwnProperty.call(kinds, operation.kind) || !fromBaseRecord(operation.row))
            throw new Error('generator:wrong-row-kind');
        if (Object.keys(operation).some(key => key !== 'kind' && key !== 'row')) throw new Error('generator:wrong-row-kind');
        const spec = kinds[operation.kind];
        const id = operation.kind === 'inventory' ? readInventoryField({ inventory: operation.row }, spec.key) : readItemField(operation.row, spec.key);
        if (!fromBaseUuid(id)) throw new Error('generator:invalid-row-identity');
        const identity = String(id).toUpperCase();
        if (seen.has(identity)) throw new Error('generator:invalid-row-identity');
        seen.add(identity);
        values(operation.kind, operation.row, spec.columns);
    }
    for (const update of command.updates ?? []) {
        if (update.table !== 'inventory' || update.op !== 'update'
            || !fromBaseUuid(readInventoryField({ inventory: update.row }, 'inventory_id')))
            throw new Error('generator:invalid-row-identity');
        const allowed = ['inventory_id', 'item_qty', 'inventory_presentation_pic_id', 'inventory_thumbnail_pic_id'];
        values('inventory', update.row, allowed);
        values('inventory', update.before ?? {}, allowed);
    }
}
